import { useEffect, useState, useCallback } from "react";
import { api } from "@/lib/api";

/**
 * Global admin data-source toggle: "preview" (local backend) vs "live" (production
 * read-only via the relay). Persisted in localStorage, broadcast across tabs +
 * components via a custom event so flipping the switch in the header instantly
 * refetches data on any admin page that subscribes.
 *
 *   const { source, setSource, isLive, relayConfigured, isPreview } = useLiveSource();
 *
 * Pages that want to react should depend on `source` in their useEffect deps.
 * Pages that *don't* know about live mode silently keep showing preview data.
 *
 * Storage key: `flexpay_admin_data_source` (value: "preview" | "live")
 */
const STORAGE_KEY = "flexpay_admin_data_source";
const EVENT_NAME = "flexpay:data-source-change";

// Module-level cache for relay status so we hit /providers exactly once per session.
let _relayStatusPromise = null;
const fetchRelayStatus = () => {
  if (!_relayStatusPromise) {
    _relayStatusPromise = api
      .get("/admin/system-test/providers")
      .then(({ data }) => ({
        relayConfigured: !!data.relay_configured,
        isPreview: data.is_preview !== false,
      }))
      .catch(() => ({ relayConfigured: false, isPreview: true }));
  }
  return _relayStatusPromise;
};

const readSource = () => {
  if (typeof window === "undefined") return "preview";
  const v = window.localStorage.getItem(STORAGE_KEY);
  return v === "live" ? "live" : "preview";
};

const writeSource = (next) => {
  if (typeof window === "undefined") return;
  if (next === "live") {
    window.localStorage.setItem(STORAGE_KEY, "live");
  } else {
    window.localStorage.removeItem(STORAGE_KEY);
  }
  window.dispatchEvent(new CustomEvent(EVENT_NAME, { detail: next }));
};

export function useLiveSource() {
  const [source, setSourceState] = useState(readSource);
  const [relayConfigured, setRelayConfigured] = useState(false);
  const [isPreview, setIsPreview] = useState(true);

  useEffect(() => {
    let alive = true;
    fetchRelayStatus().then((s) => {
      if (!alive) return;
      setRelayConfigured(s.relayConfigured);
      setIsPreview(s.isPreview);
      // Defensive: if a stale "live" choice exists but we're already on live
      // env, normalise to preview (which IS live there).
      if (!s.isPreview && source === "live") writeSource("preview");
    });
    return () => { alive = false; };
  // eslint-disable-next-line react-hooks/exhaustive-deps
  }, []);

  useEffect(() => {
    const onChange = (e) => {
      const next = e.detail || readSource();
      setSourceState(next === "live" ? "live" : "preview");
    };
    const onStorage = (e) => {
      if (e.key === STORAGE_KEY) setSourceState(readSource());
    };
    window.addEventListener(EVENT_NAME, onChange);
    window.addEventListener("storage", onStorage);
    return () => {
      window.removeEventListener(EVENT_NAME, onChange);
      window.removeEventListener("storage", onStorage);
    };
  }, []);

  const setSource = useCallback((next) => {
    const v = next === "live" ? "live" : "preview";
    if (v === "live" && !relayConfigured) return;          // blocked
    writeSource(v);
    setSourceState(v);
  }, [relayConfigured]);

  return {
    source,
    setSource,
    relayConfigured,
    isPreview,
    isLive: source === "live",
  };
}
